import React, { memo, useMemo, useCallback, DragEvent, useState, useEffect, useRef } from 'react';
import { Proposal } from '../types';
import { BlockContainer } from '../BlockContainer';
import './index.scss';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/proposal/hooks';
import { GalleryBlockSettings } from './GalleryBlockSettings';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import { Container, Carousel, CarouselItem, CarouselControl, CarouselIndicators, CarouselCaption } from 'reactstrap';
import { FormText } from '@core/components/form/FormText';
import { useDidMount } from '@core/utils/hooks/react';
import { FormHTMLInline } from '@core/components/form';

function defineGalleryBlockItemForm() {
  return formGroup({
    image: formControl<string>({
      initialValue: 'https://startuppakistan.com.pk/wp-content/uploads/2022/02/best-bicycle-696x392.jpg',
    }),
    text: formControl<string>({
      initialValue: 'Add Caption',
    }),
    menuOrder: formControl<number>({
      initialValue: 0,
    }),
  });
}

function defineGalleryBlockForm() {
  return formGroup({
    items: formArray(() => defineGalleryBlockItemForm()),
    captions: formControl<boolean>({
      initialValue: true,
      label: 'Image Captions',
    }),
    carouselHeight: formControl<number>({
      initialValue: 400,
      label: 'Carousel Height (px)',
    }),
    fullWidth: formControl<boolean>({
      initialValue: false,
      label: 'Full Width',
    }),
    size: formControl<'contain' | 'cover' | 'fill'>({
      label: 'Image Size',
      initialValue: 'cover',
    }),
    advance: defineBlockAdvanceForm(),
  });
}

export type GalleryWidgetFormData = FormDataType<typeof defineGalleryBlockForm>;
export type GalleryWidgetForm = ReturnType<typeof defineGalleryBlockForm>;

export enum GalleryBlockVariation {
  carousel = 'carousel',
  twoColumnGrid = 'twoColumnGrid',
  threeColumnGrid = 'threeColumnGrid',
  fourColumnGrid = 'fourColumnGrid',
}

const columnMap: { [key in GalleryBlockVariation]: number } = {
  [GalleryBlockVariation.carousel]: 1,
  [GalleryBlockVariation.twoColumnGrid]: 2,
  [GalleryBlockVariation.threeColumnGrid]: 3,
  [GalleryBlockVariation.fourColumnGrid]: 4,
};

export const GalleryBlock = memo((props: Proposal.BlockProps<GalleryBlockVariation>) => {
  const { variation, preview, editable } = props;
  const columns = columnMap[variation];
  const form = useMemo(defineGalleryBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const itemValues = useObservable(form.controls.items.items$, []);
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useDidMount(() => {
    for (let i = 0; i < columns; i++) {
      form.controls.items.push().patchValue({ menuOrder: i });
    }
  });
  useConfigureBlock({
    props,
    form,
    contentComponent: GalleryBlockSettings,
    contentProps: { form, variation },
  });

  const onDropImage = useCallback(
    (file: File) => {
      UploadAPI.upload('proposal/images', file)
        .pipe(tap(res => form.controls.items.push().patchValue({ image: res.fileUrl })))
        .subscribe();
    },
    [form],
  );

  // Aggressive object-fit enforcement
  const forceObjectFit = useCallback(() => {
    if (!containerRef.current) return;

    const images = containerRef.current.querySelectorAll('img');
    images.forEach((img: HTMLImageElement) => {
      // Force the style immediately
      img.style.setProperty('object-fit', formValue.size, 'important');
      img.style.setProperty('width', '100%', 'important');
      img.style.setProperty('height', '100%', 'important');
      img.style.setProperty('transition', 'none', 'important');
      img.style.setProperty('transform', 'none', 'important');
    });
  }, [formValue.size]);

  const next = () => {
    if (animating) return;
    const nextIndex = activeIndex === itemValues.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(nextIndex);
    // Force styles immediately on navigation
    setTimeout(forceObjectFit, 0);
    setTimeout(forceObjectFit, 50);
    setTimeout(forceObjectFit, 100);
  };

  const previous = () => {
    if (animating) return;
    const nextIndex = activeIndex === 0 ? itemValues.length - 1 : activeIndex - 1;
    setActiveIndex(nextIndex);
    // Force styles immediately on navigation
    setTimeout(forceObjectFit, 0);
    setTimeout(forceObjectFit, 50);
    setTimeout(forceObjectFit, 100);
  };

  const goToIndex = (newIndex: number) => {
    if (animating) return;
    setActiveIndex(newIndex);
    // Force styles immediately on navigation
    setTimeout(forceObjectFit, 0);
    setTimeout(forceObjectFit, 50);
    setTimeout(forceObjectFit, 100);
  };

  // Set up MutationObserver to watch for any DOM changes in the carousel
  useEffect(() => {
    if (!containerRef.current) return;

    // Apply styles immediately
    forceObjectFit();

    const observer = new MutationObserver(mutations => {
      let shouldUpdate = false;

      mutations.forEach(mutation => {
        if (mutation.type === 'childList' || mutation.type === 'attributes' || (mutation.target as Element).tagName === 'IMG') {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        // Apply styles immediately when DOM changes
        forceObjectFit();
        // Also apply after a tiny delay to catch any async changes
        setTimeout(forceObjectFit, 10);
        setTimeout(forceObjectFit, 50);
      }
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => observer.disconnect();
  }, [forceObjectFit]);

  const renderCarousel = () => {
    const slides = itemValues.map((ctrl, index) => {
      const objectFitClass = `object-fit-${formValue.size}`;
      return (
        <CarouselItem onExiting={() => setAnimating(true)} onExited={() => setAnimating(false)} key={index}>
          <div className="carousel-image-container" style={{ height: `${formValue.carouselHeight}px` }}>
            <img
              src={ctrl.value.image}
              alt="Gallery image"
              className={objectFitClass}
              style={{
                objectFit: formValue.size,
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            />
            {formValue.captions && <CarouselCaption captionText={<FormHTMLInline control={ctrl.controls.text} editable={editable} />} />}
          </div>
        </CarouselItem>
      );
    });

    return (
      <div style={{ position: 'relative' }}>
        <Carousel activeIndex={activeIndex} next={next} previous={previous} className="gallery-carousel" ride="carousel" interval={5000}>
          <CarouselIndicators
            items={itemValues.map((_, index) => ({ src: '', altText: '', caption: '', key: index }))}
            activeIndex={activeIndex}
            onClickHandler={goToIndex}
          />
          {slides}
        </Carousel>

        {/* Custom controls for both full-width and normal modes */}
        <button
          className="unified-carousel-control unified-carousel-prev"
          onClick={previous}
          style={{
            position: 'absolute',
            left: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1050,
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            border: '2px solid rgba(255, 255, 255, 0.9)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            fontWeight: 'bold',
          }}
        >
          ‹
        </button>

        <button
          className="unified-carousel-control unified-carousel-next"
          onClick={next}
          style={{
            position: 'absolute',
            right: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1050,
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            border: '2px solid rgba(255, 255, 255, 0.9)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            fontWeight: 'bold',
          }}
        >
          ›
        </button>
      </div>
    );
  };

  const renderGrid = () => {
    const objectFitClass = `object-fit-${formValue.size}`;
    return (
      <div
        className={`cols-${columns}`}
        style={{ display: 'grid ', gridTemplateColumns: Array(columns + 1).join('1fr '), columnGap: '1em', rowGap: '2em' }}
      >
        {itemValues.map((ctrl, i) => (
          <div className="image-centered" key={i}>
            <img
              src={ctrl.value.image}
              alt={`Gallery image ${i + 1}`}
              className={objectFitClass}
              style={{
                objectFit: formValue.size,
                width: '100%',
                height: '100%',
              }}
            />
            {formValue.captions && <FormHTMLInline control={ctrl.controls.text} editable={editable} />}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div ref={containerRef}>
      <BlockContainer
        className={classNames('block-image', variation, { preview })}
        advanceForm={formValue.advance}
        editable={editable}
        isSection={props.isSection}
        sectionTitle={props.sectionTitle}
        fullWidth={formValue.fullWidth}
        data-image-size={formValue.size}
        data-full-width={formValue.fullWidth.toString()}
      >
        {variation === GalleryBlockVariation.carousel ? renderCarousel() : renderGrid()}
      </BlockContainer>
    </div>
  );
});
